import { useState, useRef, useEffect, useCallback } from "react";
import useAxiosPrivate from "hooks/useAxiosPrivate";

const SUGGESTED_QUESTIONS = [
  "How many branches do I have?",
  "Show me total stock value by category",
  "Which products are running low on stock?",
  "How many purchases were made this month?",
];

function ThinkingDots() {
  return (
    <div className="fab-thinking">
      <span /><span /><span />
    </div>
  );
}

export default function FloatingAgent() {
  const axiosPrivate = useAxiosPrivate();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: 0,
      role: "agent",
      text: "Hi! I'm your data assistant. Ask me anything about your business.",
      timestamp: Date.now(),
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = useCallback(async (question) => {
    const q = (question ?? input).trim();
    if (!q || loading) return;
    setInput("");
    setLoading(true);

    const userMsg = { id: Date.now(), role: "user", text: q, timestamp: Date.now() };
    const thinkingMsg = { id: Date.now() + 1, role: "agent", loading: true };
    setMessages(prev => [...prev, userMsg, thinkingMsg]);

    try {
      const res = await axiosPrivate.post("/api/v1/agent/ask", { question: q });
      const data = res.data;
      setMessages(prev =>
        prev.map(m =>
          m.loading
            ? {
                id: Date.now() + 2,
                role: data.success ? "agent" : "error",
                text: data.success ? data.answer : data.errorMessage,
                timestamp: Date.now(),
              }
            : m
        )
      );
    } catch (e) {
      setMessages(prev =>
        prev.map(m =>
          m.loading
            ? {
                id: Date.now() + 2,
                role: "error",
                text: "Could not reach the server. Please try again.",
                timestamp: Date.now(),
              }
            : m
        )
      );
    } finally {
      setLoading(false);
    }
  }, [input, loading, axiosPrivate]);

  return (
    <>
      <style>{styles}</style>

      {!open && (
        <button className="fab-bubble" onClick={() => setOpen(true)} title="AI Assistant">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M12 2C6.48 2 2 6.48 2 12c0 1.54.36 2.98.97 4.29L2 22l5.71-.97C9.02 21.64 10.46 22 12 22c5.52 0 10-4.48 10-10S17.52 2 12 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      )}

      {open && (
        <div className="fab-panel">
          <div className="fab-header">
            <div>
              <div className="fab-header-title">AI Assistant</div>
              <div className="fab-header-sub">Ask about your data</div>
            </div>
            <button className="fab-close" onClick={() => setOpen(false)}>✕</button>
          </div>

          <div className="fab-messages">
            {messages.map(m => (
              <div key={m.id} className={`fab-msg ${m.role === "user" ? "fab-user" : m.role === "error" ? "fab-error" : "fab-agent"}`}>
                {m.loading ? <ThinkingDots /> : <div className="fab-msg-text">{m.text}</div>}
              </div>
            ))}
            <div ref={bottomRef} />
          </div>

          {messages.length <= 1 && (
            <div className="fab-suggestions">
              {SUGGESTED_QUESTIONS.map(q => (
                <button key={q} className="fab-sugg-chip" onClick={() => send(q)} disabled={loading}>{q}</button>
              ))}
            </div>
          )}

          <div className="fab-input-row">
            <input
              className="fab-input"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") send(); }}
              placeholder="Ask a question..."
              disabled={loading}
            />
            <button className="fab-send" onClick={() => send()} disabled={loading || !input.trim()}>➤</button>
          </div>
        </div>
      )}
    </>
  );
}

const styles = `
  .fab-bubble {
    position: fixed;
    bottom: 24px;
    right: 24px;
    width: 56px;
    height: 56px;
    border-radius: 50%;
    background: linear-gradient(135deg, #7F77DD, #4F46E5);
    border: none;
    color: #fff;
    font-size: 24px;
    cursor: pointer;
    box-shadow: 0 8px 24px rgba(0,0,0,0.4), 0 0 20px rgba(0,212,160,0.3);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
    transition: transform 0.2s;
  }
  .fab-bubble:hover {
    transform: scale(1.08);
  }

  .fab-panel {
    position: fixed;
    bottom: 90px;
    right: 24px;
    width: 380px;
    max-width: 90vw;
    height: 520px;
    max-height: 80vh;
    background: #0d0f14;
    border: 1px solid #252a38;
    border-radius: 18px;
    box-shadow: 0 20px 50px rgba(0,0,0,0.6);
    display: flex;
    flex-direction: column;
    z-index: 9999;
    font-family: 'Sora', sans-serif;
    overflow: hidden;
  }

  .fab-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px;
    background: #13161e;
    border-bottom: 1px solid #252a38;
  }
  .fab-header-title { font-size: 15px; font-weight: 700; color: #e8ecf4; }
  .fab-header-sub { font-size: 11px; color: #8892a4; margin-top: 2px; }
  .fab-close {
    background: none;
    border: none;
    color: #8892a4;
    font-size: 18px;
    cursor: pointer;
    padding: 4px 8px;
    border-radius: 6px;
  }
  .fab-close:hover { color: #fff; background: #1a1e28; }

  .fab-messages {
    flex: 1;
    overflow-y: auto;
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 14px;
  }
  .fab-msg {
    max-width: 90%;
    padding: 10px 14px;
    border-radius: 12px;
    font-size: 13px;
    line-height: 1.5;
  }
  .fab-agent {
    align-self: flex-start;
    background: #13161e;
    border: 1px solid #252a38;
    color: #e8ecf4;
  }
  .fab-user {
    align-self: flex-end;
    background: #1E1B4B;
    border: 1px solid #4F46E544;
    color: #e8ecf4;
  }
  .fab-error {
    align-self: flex-start;
    background: #1F1418;
    border: 1px solid #DC262644;
    color: #FCA5A5;
  }
  .fab-msg-text { margin-bottom: 0; }

  .fab-suggestions {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    padding: 8px 16px;
    border-top: 1px solid #252a38;
  }
  .fab-sugg-chip {
    background: #1a1e28;
    border: 1px solid #252a38;
    color: #8892a4;
    padding: 5px 10px;
    border-radius: 14px;
    font-size: 11px;
    cursor: pointer;
    transition: all 0.15s;
  }
  .fab-sugg-chip:hover { border-color: #7F77DD; color: #7F77DD; }

  .fab-input-row {
    display: flex;
    gap: 8px;
    padding: 12px 16px;
    border-top: 1px solid #252a38;
    background: #13161e;
  }
  .fab-input {
    flex: 1;
    background: #1a1e28;
    border: 1px solid #252a38;
    color: #e8ecf4;
    border-radius: 10px;
    padding: 10px 14px;
    font-size: 13px;
    outline: none;
  }
  .fab-input::placeholder { color: #8892a4; }
  .fab-send {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    background: #7F77DD;
    border: none;
    color: #0d0f14;
    font-size: 16px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
  }
  .fab-send:hover:not(:disabled) { background: #00f5b8; }
  .fab-send:disabled { opacity: 0.4; cursor: not-allowed; }

  .fab-thinking {
    display: flex;
    gap: 5px;
    padding: 4px 0;
  }
  .fab-thinking span {
    width: 6px; height: 6px; border-radius: 50%;
    background: #7F77DD;
    animation: fab-bounce 1.2s ease-in-out infinite;
  }
  .fab-thinking span:nth-child(2) { animation-delay: 0.2s; background: #4F46E5; }
  .fab-thinking span:nth-child(3) { animation-delay: 0.4s; background: #8892a4; }
  @keyframes fab-bounce {
    0%,80%,100% { transform: translateY(0); opacity:0.4; }
    40% { transform: translateY(-6px); opacity:1; }
  }
`;